import goodsApi from '@/api/goods.js';
import {
  formatSeckillActivityPriceOnlyDisplay,
  mergeActivitySkuIntoSkuPrices,
  normalizeGoodsSkuPriceArrays,
} from '@/units/activityGoodsPrice.js';

/** 列表项是否已能展示秒杀价（activityList 经 recover 后通常已满足） */
export function hasDisplayableSeckillPrice(goods) {
  const g = normalizeGoodsSkuPriceArrays(goods);
  if (!g) return false;
  return !!formatSeckillActivityPriceOnlyDisplay(g);
}

export async function mapWithConcurrency(items, mapper, limit = 4) {
  if (!items || !items.length) return [];
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const idx = cursor;
      cursor += 1;
      results[idx] = await mapper(items[idx], idx);
    }
  };
  const n = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
  return results;
}

/**
 * 详情里的 activity.activity_sku_prices 常为空，真实活动价可能在 goods.goods/activity 接口。
 */
export async function mergeActivitySkuPricesFromActivityEndpoint(data, activityId) {
  if (!data || data.activity_type !== 'seckill' || !activityId || data.id == null) return data;
  if (hasDisplayableSeckillPrice(data)) return data;
  try {
    const res = await goodsApi.activity({
      activity_id: activityId,
      goods_id: data.id,
      id: data.id,
    });
    const ag = res && res.data;
    if (!ag || typeof ag !== 'object') return data;
    const extra =
      (Array.isArray(ag.activity_sku_prices) && ag.activity_sku_prices.length && ag.activity_sku_prices) ||
      (ag.activity &&
        Array.isArray(ag.activity.activity_sku_prices) &&
        ag.activity.activity_sku_prices.length &&
        ag.activity.activity_sku_prices) ||
      null;
    if (!extra || !extra.length) return data;
    let out = mergeActivitySkuIntoSkuPrices({
      ...data,
      activity_sku_prices: extra,
      activity_type: 'seckill',
    });
    out = normalizeGoodsSkuPriceArrays(out);
    if (ag.min_activity_price != null && ag.min_activity_price !== '') {
      out = { ...out, min_activity_price: ag.min_activity_price };
    }
    if (ag.max_activity_price != null && ag.max_activity_price !== '') {
      out = { ...out, max_activity_price: ag.max_activity_price };
    }
    return out;
  } catch (e) {
    return data;
  }
}

function unwrapActivityListGoodsItem(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const nested = raw.goods && typeof raw.goods === 'object' ? raw.goods : {};
  const merged = { ...nested, ...raw };
  const id = merged.id ?? merged.goods_id ?? nested.id;
  if (id != null) merged.id = id;
  return merged;
}

function mergeListItemWithDetail(item, d) {
  const skuRows = d.sku_prices ?? d.new_sku_prices;
  const merged = {
    ...item,
    price: d.price != null ? d.price : item.price,
    original_price: d.original_price != null ? d.original_price : item.original_price,
    original_goods_price:
      d.original_goods_price != null ? d.original_goods_price : item.original_goods_price,
    new_sku_prices: skuRows != null ? skuRows : item.new_sku_prices,
    sku_prices: skuRows != null ? skuRows : item.sku_prices,
    activity_type: d.activity_type || item.activity_type || 'seckill',
    is_sku: d.is_sku != null ? d.is_sku : item.is_sku,
    activity: d.activity != null ? d.activity : item.activity,
    min_activity_price:
      d.min_activity_price != null && d.min_activity_price !== ''
        ? d.min_activity_price
        : item.min_activity_price,
    max_activity_price:
      d.max_activity_price != null && d.max_activity_price !== ''
        ? d.max_activity_price
        : item.max_activity_price,
  };
  return normalizeGoodsSkuPriceArrays(merged);
}

/**
 * activityList 缺活动价时再拉详情补全；已有秒杀价则跳过请求。
 */
export async function enrichGoodsWithActivityDetail(goodsItem, activityId) {
  let item = unwrapActivityListGoodsItem(goodsItem);
  if (!item) return goodsItem;
  item = normalizeGoodsSkuPriceArrays(item);
  const gid = item.id ?? item.goods_id;
  if (gid == null) return goodsItem;
  if (!activityId) return item;
  if (hasDisplayableSeckillPrice(item)) return item;
  try {
    const res = await goodsApi.detail(gid, { activity_id: activityId });
    let d = mergeActivitySkuIntoSkuPrices(res && res.data ? { ...res.data } : null);
    d = normalizeGoodsSkuPriceArrays(d);
    if (!d) return item;
    if (!hasDisplayableSeckillPrice(d)) {
      d = await mergeActivitySkuPricesFromActivityEndpoint(d, activityId);
    }
    return mergeListItemWithDetail(item, d);
  } catch (e) {
    return item;
  }
}

/**
 * 仅对缺活动价的商品补全，且限制并发，避免秒杀列表页 N×2 请求卡死。
 */
export async function enrichGoodsListWithActivityDetail(list, activityId, concurrency = 4) {
  if (!Array.isArray(list) || !list.length || !activityId) return list;
  const normalized = list.map((raw) =>
    normalizeGoodsSkuPriceArrays(unwrapActivityListGoodsItem(raw))
  );
  const pending = [];
  const pendingIdx = [];
  normalized.forEach((g, i) => {
    if (!hasDisplayableSeckillPrice(g)) {
      pending.push(g);
      pendingIdx.push(i);
    }
  });
  if (!pending.length) return normalized;
  const filled = await mapWithConcurrency(
    pending,
    (g) => enrichGoodsWithActivityDetail(g, activityId),
    concurrency
  );
  const out = normalized.slice();
  pendingIdx.forEach((origI, j) => {
    out[origI] = filled[j];
  });
  return out;
}

/** 规范化 activityList 行（不发起网络请求） */
export function normalizeActivityListRows(list) {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((raw) => {
    const item = unwrapActivityListGoodsItem(raw);
    return normalizeGoodsSkuPriceArrays({
      ...item,
      activity_type: item.activity_type || 'seckill',
    });
  });
}
